/**
 * FacialMap — mapa facial persistente del paciente (no por episodio).
 *
 * Una foto frontal de cara (hombre / mujer, según el sexo del paciente) sobre
 * la que el doctor marca puntos (rojo/amarillo) haciendo clic, con una nota por
 * punto y anotaciones generales. Sirve para dejar registrado dónde se puso
 * relleno, grasa (lipofilling) o cirugía.
 *
 * Se guarda en el propio paciente (`patient.facial_map`) — el modelo de
 * paciente acepta campos extra, así que persiste con un PUT normal y queda
 * fijo en la historia clínica, visible siempre. Los marcadores se guardan en %
 * (0..100) para ser independientes del tamaño de la imagen en pantalla.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Trash2, MapPin, ChevronDown, ChevronUp, Eraser } from 'lucide-react';
import { api, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { isManagement } from '../../lib/roles';
import ProductosMapaDialog, { URL_PRODUCTOS_MAPA } from './ProductosMapaDialog';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import faceMale from '../../assets/face_male.jpg';
import faceFemale from '../../assets/face_female.jpg';

// Qué se ha puesto en cada punto. El color es solo la forma de verlo: lo que
// se guarda es el PRODUCTO (2026-08-19). Los productos son de CADA CLÍNICA
// —sus tratamientos— y vienen de la base (GET /api/mapa-facial/productos); los
// edita gestión desde el propio mapa. El primero es el marcador por defecto.
// Sin productos se pueden poner puntos igual, sin clasificar.
const COLOR_SIN_PRODUCTO = '#94a3b8';

// Los puntos de antes se guardaron como 'rojo'/'amarillo'. Se siguen viendo
// con su color; no se reescribe la base — un punto viejo se queda como está
// hasta que alguien lo cambie a mano.
const COLORES_ANTIGUOS = { rojo: '#e11d48', amarillo: '#f59e0b' };

/** El producto de un punto (null si no es de ninguno del catálogo actual). */
function productoDe(marker, productos) {
  const c = marker?.producto || marker?.color || '';
  return (productos || []).find((p) => p.id === c) || null;
}

/** El color con el que se pinta: el de su producto, el de un punto antiguo
 *  o gris si su producto ya no está en el catálogo. */
function colorDe(marker, productos) {
  return productoDe(marker, productos)?.color || COLORES_ANTIGUOS[marker?.color] || COLOR_SIN_PRODUCTO;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Normaliza el sexo a 'M' (hombre) / 'F' (mujer). Admite los valores de la
 *  LOPD ("M"/"F"), de una importación ("Masculino"/"Femenino") y variantes. */
function normSexo(v) {
  const s = String(v || '').trim().toLowerCase();
  if (!s) return '';
  if (s.startsWith('f') || s.startsWith('muj') || s.startsWith('wom')) return 'F';
  if (s.startsWith('m') || s.startsWith('h') || s.startsWith('v')) return 'M';
  return '';
}

export default function FacialMap({ patient, onPatientUpdated, apilado = false }) {
  const { user } = useAuth();
  const fm = patient.facial_map || {};
  const [markers, setMarkers] = useState(fm.markers || []);
  const [notes, setNotes] = useState(fm.notes || '');
  const [sexo, setSexo] = useState(fm.sexo || normSexo(patient.sexo) || 'F');
  const [productos, setProductos] = useState([]);
  const [producto, setProducto] = useState('');
  const [editarProductos, setEditarProductos] = useState(false);
  const esGestion = isManagement(user);
  const cargarProductos = useCallback(() => {
    api.get(URL_PRODUCTOS_MAPA)
      .then((r) => {
        const lista = Array.isArray(r.data?.productos) ? r.data.productos : [];
        setProductos(lista);
        // El marcador elegido sigue si existe; si no, el primero del catálogo.
        setProducto((actual) => (lista.some((p) => p.id === actual) ? actual : (lista[0]?.id || '')));
      })
      .catch(() => setProductos([]));
  }, []);
  useEffect(() => { cargarProductos(); }, [cargarProductos]);
  const [open, setOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Versión de cambios: si el usuario marca un punto MIENTRAS un guardado
  // está en vuelo, el éxito de ese guardado no debe limpiar dirty
  // (revisión 2026-08-27: el punto se perdía en el modo apilado).
  const versionRef = useRef(0);
  // Foto del paciente como fondo del mapa (2026-07-17): se puede marcar sobre la
  // foto REAL — la del "antes" o la "comparativa" (collage) — o sobre la genérica.
  const [fotos, setFotos] = useState([]);
  const [fondo, setFondo] = useState(fm.fondo || (fm.usar_foto === false ? 'generica' : 'antes'));
  // Qué foto de «antes» ve el médico en el mapa (2026-08-27): puede
  // elegir entre las que subió el paciente (frontal/oblicua), pero la
  // preferencia por defecto es SIEMPRE la frontal.
  const [fotoAntesId, setFotoAntesId] = useState(fm.foto_antes_id || null);
  const boxRef = useRef(null);

  // Reengancha cuando cambia el paciente o llega una actualización.
  useEffect(() => {
    const f = patient.facial_map || {};
    setMarkers(f.markers || []);
    setNotes(f.notes || '');
    setSexo(f.sexo || normSexo(patient.sexo) || 'F');
    setFondo(f.fondo || (f.usar_foto === false ? 'generica' : 'antes'));
    setFotoAntesId(f.foto_antes_id || null);
    setDirty(false);
  }, [patient.id, patient.facial_map, patient.sexo]);

  // Carga las fotos del paciente (para usar "antes" o "comparativa" de fondo).
  useEffect(() => {
    let cancelled = false;
    api.get(`/api/patients/${patient.id}/fotos`)
      .then((r) => { if (!cancelled) setFotos(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setFotos([]); });
    return () => { cancelled = true; };
  }, [patient.id]);

  // La lista viene ordenada por fecha desc: la primera de cada tipo es la más reciente.
  // La foto de fondo por defecto es la FRONTAL (2026-08-25): el
  // enlace sube frontal y oblicua, y como la oblicua entra la última quedaba
  // ella delante. Se busca primero una 'antes' frontal (por su pie de foto);
  // si no la hay, la 'antes' más reciente, como siempre.
  const deAntes = useMemo(() => fotos.filter((f) => (f.tipo || '') === 'antes'), [fotos]);
  const antesFoto = useMemo(() => {
    const elegida = fotoAntesId ? deAntes.find((f) => f.id === fotoAntesId) : null;
    return elegida || deAntes.find((f) => /frontal/i.test(f.caption || '')) || deAntes[0] || null;
  }, [deAntes, fotoAntesId]);
  const etiquetaFoto = (f, i) => {
    if (/frontal/i.test(f.caption || '')) return 'Frontal';
    if (/oblicua|45/i.test(f.caption || '')) return 'Oblicua';
    return `Foto ${i + 1}`;
  };
  const compFoto = useMemo(() => fotos.find((f) => (f.tipo || '') === 'comparativa') || null, [fotos]);
  const fotoFondo = fondo === 'comparativa' ? compFoto : fondo === 'antes' ? antesFoto : null;
  const usandoFoto = !!fotoFondo;
  // `?tam=mapa` (revisión 24/9/2026): la foto COMO SE GUARDÓ, que es sobre la
  // que se pusieron los puntos, y en tamaño medio (1200 px, no la de hasta
  // 10 MB). Sin él, `/img` sirve la foto ENTERA, y en las que se recortaron
  // a la cara el recuadro pasaba de 4:5 a 3:4, la cara salía
  // más pequeña y los puntos de infiltración caían en otro sitio.
  const fotoUrl = fotoFondo ? `${BACKEND_URL}/api/patients/${patient.id}/fotos/${fotoFondo.id}/img?tam=mapa` : null;

  const meta = fm.updated_at
    ? `Última edición: ${formatDateTime(fm.updated_at)}${fm.updated_by_name ? ` · ${fm.updated_by_name}` : ''}`
    : null;

  const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `m${Date.now()}${Math.random().toString(36).slice(2, 7)}`);

  const faceSrc = sexo === 'M' ? faceMale : faceFemale;

  const addMarker = (e) => {
    const box = boxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setMarkers((prev) => [...prev, { id: uid(), x, y, producto: producto || null, color: producto || null, note: '' }]);
    versionRef.current += 1;
    setDirty(true);
  };

  const setNote = (id, note) => {
    setMarkers((prev) => prev.map((m) => (m.id === id ? { ...m, note } : m)));
    setDirty(true);
  };
  const removeMarker = (id) => {
    setMarkers((prev) => prev.filter((m) => m.id !== id));
    setDirty(true);
  };
  const clearAll = () => {
    if (!markers.length) return;
    if (!window.confirm('¿Borrar todos los puntos del mapa facial?')) return;
    setMarkers([]);
    setDirty(true);
  };

  const changeSexo = (s) => {
    if (s === sexo) return;
    setSexo(s);
    setDirty(true);
  };

  // Junto al evolutivo no hay botón Guardar (nada bajo la cara): cada
  // cambio se guarda solo, con un respiro para agrupar clics seguidos.
  useEffect(() => {
    if (!apilado || !dirty || saving) return undefined;
    const t = setTimeout(() => { save(); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apilado, dirty, markers, notes, fondo, sexo, fotoAntesId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        facial_map: {
          markers,
          notes,
          sexo,
          fondo,
          usar_foto: fondo !== 'generica',
          foto_id: fotoFondo ? fotoFondo.id : null,
          foto_antes_id: (fotoAntesId && deAntes.some((f) => f.id === fotoAntesId)) ? fotoAntesId : null,
          updated_at: new Date().toISOString(),
          updated_by_name: user?.name || '',
        },
      };
      const versionEnviada = versionRef.current;
      await api.put(`/api/patients/${patient.id}`, payload);
      toast.success('Mapa facial guardado');
      if (versionRef.current === versionEnviada) setDirty(false);
      onPatientUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el mapa facial');
    } finally {
      setSaving(false);
    }
  };

  // Cuántos puntos hay de cada producto, para el título.
  const resumen = useMemo(() => productos
    .map((pr) => ({ ...pr, n: markers.filter((m) => productoDe(m, productos)?.id === pr.id).length }))
    .filter((x) => x.n > 0), [markers, productos]);

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#2E6E5E]" /> Mapa facial
          {markers.length > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
              {resumen.map((x) => (
                <span key={x.id} className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: x.color }} />
                  {x.n} {x.corto}
                </span>
              ))}
            </span>
          )}
        </span>
      }
      action={
        <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)} data-testid="facial-map-toggle">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {open ? 'Ocultar' : 'Abrir'}
        </Button>
      }
    >
      {!open ? (
        <p className="text-sm text-muted-foreground">
          Marca en la cara dónde se puso relleno, grasa o cirugía (queda fijo en la historia). Pulsa «Abrir» para editarlo.
        </p>
      ) : (
        <div className={apilado ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 lg:grid-cols-2 gap-4'}>
          {/* `apilado`: junto al evolutivo la columna es estrecha; partirla en
              dos dejaba la cara diminuta y los puntos salían gordos. La cara a
              todo el ancho y la lista debajo (2026-08-19: «que la
              cara ocupe más espacio para marcar más preciso»). */}
          {/* Cara + marcadores */}
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs text-muted-foreground">Qué pongo:</span>
              {productos.length === 0 && (
                <span className="text-xs text-muted-foreground" data-testid="facial-map-sin-productos">
                  Sin productos configurados: los puntos quedan sin clasificar.
                </span>
              )}
              {esGestion && (
                <button type="button" onClick={() => setEditarProductos(true)}
                  className="text-xs underline text-[#2E6E5E]" data-testid="facial-map-editar-productos">
                  {productos.length ? 'Editar productos' : 'Configurar productos'}
                </button>
              )}
              {productos.map((pr) => (
                <button
                  key={pr.id}
                  type="button"
                  onClick={() => setProducto(pr.id)}
                  data-testid={`facial-map-producto-${pr.id}`}
                  title={`Marcar puntos de ${pr.label}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                    producto === pr.id ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)] ring-1 ring-[#13231E]' : 'border-[#D9E3DD]'
                  }`}
                >
                  <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: pr.color }} />
                  {pr.label}
                </button>
              ))}
              {/* Fondo del mapa: foto "antes", "comparativa" (collage) o cara genérica */}
              <div className="ml-auto inline-flex items-center gap-1 flex-wrap justify-end">
                {(antesFoto || compFoto) && (
                  <>
                    <span className="text-xs text-muted-foreground mr-0.5">Fondo:</span>
                    {[
                      ...(antesFoto ? [['antes', 'Foto antes']] : []),
                      ...(compFoto ? [['comparativa', 'Comparativa']] : []),
                      ['generica', 'Cara genérica'],
                    ].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => { setFondo(val); setDirty(true); }}
                        data-testid={`facial-map-fondo-${val}`}
                        className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                          (usandoFoto ? fondo : 'generica') === val
                            ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)]'
                            : 'border-[#D9E3DD]'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </>
                )}
                {/* Cara (hombre/mujer) solo cuando se usa la cara genérica */}
                {!usandoFoto && (
                  <>
                    <span className="text-xs text-muted-foreground mr-0.5 ml-1">Cara:</span>
                    {[['F', 'Mujer'], ['M', 'Hombre']].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => changeSexo(val)}
                        data-testid={`facial-map-sexo-${val}`}
                        className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                          sexo === val ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)]' : 'border-[#D9E3DD]'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
            {usandoFoto && fondo === 'antes' && deAntes.length > 1 && (
              <div className="flex items-center gap-1 justify-end flex-wrap mb-1.5">
                <span className="text-xs text-muted-foreground mr-0.5">Foto:</span>
                {deAntes.map((f, i) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => { setFotoAntesId(f.id); setDirty(true); }}
                    data-testid={`facial-map-foto-${i}`}
                    title={f.caption || ''}
                    className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                      antesFoto?.id === f.id
                        ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)]'
                        : 'border-[#D9E3DD]'
                    }`}
                  >
                    {etiquetaFoto(f, i)}
                  </button>
                ))}
              </div>
            )}
            <div className="rounded-lg border border-[#D9E3DD] bg-white overflow-hidden">
              <div
                ref={boxRef}
                onClick={addMarker}
                className="relative w-full max-w-[340px] mx-auto cursor-crosshair select-none"
                data-testid="facial-map-canvas"
              >
                <img
                  src={usandoFoto ? fotoUrl : faceSrc}
                  alt={usandoFoto
                    ? (fondo === 'comparativa' ? 'Comparativa del paciente' : 'Foto del paciente (antes)')
                    : (sexo === 'M' ? 'Cara de hombre' : 'Cara de mujer')}
                  className="w-full block pointer-events-none select-none"
                  draggable={false}
                />
                {markers.map((m, i) => (
                  <span
                    key={m.id}
                    className="absolute -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 border-white inline-flex items-center justify-center text-[10px] font-bold text-white shadow pointer-events-none"
                    style={{ left: `${m.x}%`, top: `${m.y}%`, backgroundColor: colorDe(m, productos) }}
                  >
                    {i + 1}
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-1.5 flex items-center gap-3">
              {!apilado && <span className="text-[11px] text-muted-foreground">
                {usandoFoto ? 'Haz clic en la foto del paciente para marcar' : 'Haz clic en la cara para marcar'}
              </span>}
              {markers.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-800"
                >
                  <Eraser className="w-3.5 h-3.5" /> Borrar todos los puntos
                </button>
              )}
            </div>
          </div>

          {/* Lista de puntos + anotaciones — junto al evolutivo NO se pinta
              nada bajo la cara (2026-08-20): el mapa guarda solo y
              las anotaciones se editan en la vista completa de abajo. */}
          {!apilado && <div className="space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
                Puntos marcados
              </div>
              {markers.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aún no hay puntos. Haz clic en la cara.</p>
              ) : (
                <ol className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {markers.map((m, i) => (
                    <li key={m.id} className="flex items-center gap-2">
                      <span
                        className="w-5 h-5 shrink-0 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ backgroundColor: colorDe(m, productos) }}
                      >
                        {i + 1}
                      </span>
                      <input
                        value={m.note}
                        onChange={(e) => setNote(m.id, e.target.value)}
                        placeholder="Qué se puso aquí (ej.: relleno, grasa, cirugía…)"
                        className="flex-1 min-w-0 h-8 rounded-md border border-[#dbe3ee] px-2 text-xs"
                        data-testid={`facial-map-note-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeMarker(m.id)}
                        className="shrink-0 text-muted-foreground hover:text-red-700"
                        title="Quitar punto"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                Anotaciones
              </div>
              <Textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
                rows={3}
                placeholder="Notas generales del mapa facial…"
                className="text-xs"
                data-testid="facial-map-notes"
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-muted-foreground">{meta}</span>
              <Button
                size="sm"
                onClick={save}
                disabled={saving || !dirty}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                data-testid="facial-map-save"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Guardar
              </Button>
            </div>
          </div>}
        </div>
      )}
      {editarProductos && (
        <ProductosMapaDialog productos={productos} onClose={() => setEditarProductos(false)}
          onGuardados={() => { setEditarProductos(false); cargarProductos(); }} />
      )}
    </SectionCard>
  );
}
